// Editor.js block tool for embedding a photo gallery managed on the portal's
// Galleries page. Stores { galleryId, layout } — the published site resolves
// the id against the build feed's galleries and renders a grid (click to
// enlarge) or a slideshow (large view + thumbnails). Rendered by the template's
// Gallery.astro, not inline markup, so the editor shows a read-only preview.
//
// config: { listGalleries }
//   listGalleries() -> { galleries: [{ id, name, images }], assetsBaseUrl }

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg>';

const LAYOUTS = [
  { value: "grid", label: "Grid" },
  { value: "masonry", label: "Masonry" },
  { value: "slideshow", label: "Slideshow" },
];

const PREVIEW_LIMIT = 8;

export class GalleryTool {
  static get toolbox() {
    return { title: "Gallery", icon: ICON };
  }

  constructor({ data, config, block }) {
    this.block = block;
    this.config = config || {};
    this.data = {
      galleryId: typeof data?.galleryId === "string" ? data.galleryId : "",
      layout: LAYOUTS.some((l) => l.value === data?.layout) ? data.layout : "grid",
    };
    this._galleries = [];
    this._assetsBaseUrl = null;
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-gallery-tool";

    const bar = document.createElement("div");
    bar.className = "pgs-gallery-tool__bar";
    const title = document.createElement("span");
    title.className = "pgs-gallery-tool__title";
    title.textContent = "Gallery";

    // Gallery picker — populated once the list loads.
    const select = document.createElement("select");
    select.className = "pgs-gallery-tool__select";
    select.setAttribute("aria-label", "Gallery");
    select.addEventListener("change", () => {
      this.data.galleryId = select.value;
      this._renderPreview();
      this.block?.dispatchChange?.();
    });
    this._select = select;

    // Grid / slideshow toggle.
    const toggle = document.createElement("div");
    toggle.className = "pgs-gallery-tool__toggle";
    this._layoutButtons = LAYOUTS.map(({ value, label }) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "pgs-gallery-tool__btn";
      button.textContent = label;
      button.addEventListener("click", () => {
        this.data.layout = value;
        this._applyLayout();
        if (this._loaded) this._renderPreview();
        this.block?.dispatchChange?.();
      });
      toggle.appendChild(button);
      return { value, button };
    });
    this._applyLayout();

    bar.append(title, select, toggle);

    const preview = document.createElement("div");
    preview.className = "pgs-gallery-tool__preview";
    this._preview = preview;

    root.append(bar, preview);

    this._loadGalleries();

    return root;
  }

  async _loadGalleries() {
    this._preview.textContent = "Loading galleries…";
    try {
      const data = await this.config.listGalleries?.();
      this._galleries = data?.galleries ?? [];
      this._assetsBaseUrl = data?.assetsBaseUrl ?? null;
    } catch (err) {
      this._preview.textContent = `Couldn't load galleries: ${err.message}`;
      return;
    }
    this._loaded = true;
    this._renderSelect();
    this._renderPreview();
  }

  _renderSelect() {
    const select = this._select;
    select.innerHTML = "";

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = this._galleries.length ? "Choose a gallery…" : "No galleries yet";
    select.appendChild(placeholder);

    for (const gallery of this._galleries) {
      const option = document.createElement("option");
      option.value = gallery.id;
      option.textContent = `${gallery.name} (${gallery.images.length})`;
      select.appendChild(option);
    }

    // A stored id whose gallery was deleted: keep it selectable so the block
    // isn't stuck, but flag it.
    const missing = this.data.galleryId && !this._galleries.some((g) => g.id === this.data.galleryId);
    if (missing) {
      const option = document.createElement("option");
      option.value = this.data.galleryId;
      option.textContent = "(gallery deleted)";
      select.appendChild(option);
    }

    select.value = this.data.galleryId;
  }

  _applyLayout() {
    for (const { value, button } of this._layoutButtons) {
      button.classList.toggle("pgs-gallery-tool__btn--active", this.data.layout === value);
    }
  }

  _renderPreview() {
    const preview = this._preview;
    preview.innerHTML = "";

    if (!this.data.galleryId) {
      preview.textContent = this._galleries.length
        ? "Choose a gallery to show here."
        : "No galleries yet — create one on the Galleries page first.";
      return;
    }

    const gallery = this._galleries.find((g) => g.id === this.data.galleryId);
    if (!gallery) {
      preview.textContent = "This gallery no longer exists — choose another.";
      return;
    }
    if (!gallery.images.length) {
      preview.textContent = `"${gallery.name}" has no photos yet — add some on the Galleries page.`;
      return;
    }

    const strip = document.createElement("div");
    strip.className = "pgs-gallery-tool__strip";
    for (const image of gallery.images.slice(0, PREVIEW_LIMIT)) {
      const key = image.thumbKey ?? image.key;
      if (!this._assetsBaseUrl || !key) continue;
      const img = document.createElement("img");
      img.src = `${this._assetsBaseUrl}/${key}`;
      img.alt = image.alt || "";
      img.loading = "lazy";
      strip.appendChild(img);
    }
    const summary = document.createElement("p");
    summary.className = "pgs-gallery-tool__summary";
    const extra = gallery.images.length - PREVIEW_LIMIT;
    summary.textContent =
      `${gallery.images.length} photo${gallery.images.length === 1 ? "" : "s"}` +
      (extra > 0 ? ` (${extra} more not shown here)` : "") +
      ` — shown as a ${this.data.layout}.`;
    preview.append(strip, summary);
  }

  save() {
    return { galleryId: this.data.galleryId, layout: this.data.layout };
  }
}
